/**
 * Dejavoo Card Entry Component
 *
 * Uses the Freedom to Design (FTD) SDK for card-not-present transactions.
 * This component renders card entry fields and handles tokenization.
 *
 * SDK Docs: https://docs.ipospays.com/freedom-to-design/apidocs
 */

import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { Loading } from '@chiroup/components/Loading';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

// FTD SDK URLs
const FTD_SANDBOX_URL = 'https://payment.ipospays.tech/ftd/v1/freedomtodesign.js';
const FTD_PROD_URL = 'https://payment.ipospays.com/ftd/v1/freedomtodesign.js';

// Global state to track SDK loading - outside React's lifecycle
let ftdSdkLoadedToken: string | null = null;
let ftdSdkLoadingPromise: Promise<void> | null = null;

interface DejavooCardEntryProps {
  ecommToken: string;
  onTokenReceived: (paymentTokenId: string, cardData: CardData) => void;
  onError: (error: string) => void;
  isSubmitting?: boolean;
  disabled?: boolean;
  defaultCardholderName?: string;
  defaultPostalCode?: string;
  showSaveCard?: boolean;
  onSaveCardChange?: (saveCard: boolean) => void;
}

interface CardData {
  cardholderFirstName: string;
  cardholderLastName: string;
  postalCode: string;
  // Last 4 digits + detected brand (Visa / Mastercard / Amex / Discover) so
  // the backend can persist something meaningful in DejavooCustomerTokens
  // without doing a separate $0 preauth. Derived from the card input on the
  // client; PAN never crosses the wire.
  cardLastFour: string;
  cardType: string;
}

/**
 * Detect the card brand from a BIN prefix. Returns a short brand label matching
 * the values the EHR's DejavooCustomerTokens column stores. Unknowns fall back
 * to 'Card' so downstream UI still has something to render.
 */
function detectCardType(pan: string): string {
  const digits = pan.replace(/\D/g, '');
  if (!digits) return 'Card';
  if (/^4/.test(digits)) return 'Visa';
  if (/^(5[1-5]|2[2-7])/.test(digits)) return 'Mastercard';
  if (/^3[47]/.test(digits)) return 'Amex';
  if (/^6(?:011|5)/.test(digits)) return 'Discover';
  if (/^35/.test(digits)) return 'JCB';
  if (/^3(?:0[0-5]|[68])/.test(digits)) return 'Diners';

  return 'Card';
}

// Declare global FTD SDK types
// The SDK exposes postData() as a global function for tokenization
declare global {
  interface Window {
    postData?: () => Promise<FTDPostDataResponse>;
    // Some SDKs read security_key from a global variable
    security_key?: string;
    ftd_security_key?: string;
  }
}

interface FTDPostDataResponse {
  payment_token_id?: string;
  error?: string;
  message?: string;
}

// Ref type for parent components to trigger tokenization.
// tokenize() returns the token + card data so callers can `await` the result
// inline (e.g. the scheduler's confirm flow, which needs the token before
// firing the appointment-create request). Returns null on failure — the
// onError callback still fires in that case.
export interface DejavooCardEntryRef {
  tokenize: () => Promise<{
    paymentTokenId: string;
    cardData: CardData;
  } | null>;
}

const DejavooCardEntry = forwardRef<DejavooCardEntryRef, DejavooCardEntryProps>(
  function DejavooCardEntry(
    {
      ecommToken,
      onTokenReceived,
      onError,
      isSubmitting = false,
      disabled = false,
      defaultCardholderName = '',
      defaultPostalCode = '',
      showSaveCard = true,
      onSaveCardChange,
    },
    ref
  ) {
    const [isLoading, setIsLoading] = useState(true);
    const [sdkLoaded, setSdkLoaded] = useState(false);
    const [sdkError, setSdkError] = useState<string | null>(null);
    const [cardholderFirstName, setCardholderFirstName] = useState(
      defaultCardholderName.split(' ')[0] || ''
    );
    const [cardholderLastName, setCardholderLastName] = useState(
      defaultCardholderName.split(' ').slice(1).join(' ') || ''
    );
    const [postalCode, setPostalCode] = useState(defaultPostalCode);
    const [saveCard, setSaveCard] = useState(false);
    const scriptRef = useRef<HTMLScriptElement | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Determine which SDK URL to use based on environment
    const getSdkUrl = useCallback(() => {
      const isProd =
        window.location.hostname === 'app.chiroup.com' || window.location.hostname.includes('prod');

      return isProd ? FTD_PROD_URL : FTD_SANDBOX_URL;
    }, []);

    // Track if elements are ready
    const [elementsReady, setElementsReady] = useState(false);

    // Mark elements as ready after first render
    useEffect(() => {
      // Small delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        setElementsReady(true);
      }, 100);

      return () => clearTimeout(timer);
    }, []);

    // Load the FTD SDK script after elements are ready
    useEffect(() => {
      if (!ecommToken) {
        setSdkError('Ecomm token is required for manual entry');
        setIsLoading(false);

        return;
      }

      if (!elementsReady) {
        return; // Wait for elements to be in the DOM
      }

      // If SDK already loaded with same token, just mark as ready
      if (ftdSdkLoadedToken === ecommToken && window.postData) {
        console.log('[FTD] SDK already loaded with same token, reusing');
        setIsLoading(false);
        setSdkLoaded(true);

        return;
      }

      // If SDK is currently loading, wait for it
      if (ftdSdkLoadingPromise && ftdSdkLoadedToken === ecommToken) {
        console.log('[FTD] SDK loading in progress, waiting...');
        ftdSdkLoadingPromise.then(() => {
          setIsLoading(false);
          setSdkLoaded(true);
        });

        return;
      }

      // Need to load SDK with new token - remove old script if exists
      const existingScript = document.getElementById('ftd');

      if (existingScript) {
        console.log('[FTD] Removing old SDK script');
        existingScript.remove();
        window.postData = undefined;
        ftdSdkLoadedToken = null;
      }

      setIsLoading(true);
      setSdkError(null);

      // Set security_key as global variables (SDK might read from these)
      window.security_key = ecommToken;
      window.ftd_security_key = ecommToken;

      const script = document.createElement('script');
      script.id = 'ftd';
      script.src = getSdkUrl();
      // Set multiple attribute variations the SDK might look for
      script.setAttribute('security_key', ecommToken);
      script.setAttribute('data-security-key', ecommToken);
      script.setAttribute('data-security_key', ecommToken);
      scriptRef.current = script;

      ftdSdkLoadingPromise = new Promise<void>((resolve, reject) => {
        script.onload = () => {
          console.log('[FTD] SDK script loaded');
          // Give the SDK time to initialize
          setTimeout(() => {
            ftdSdkLoadedToken = ecommToken;
            ftdSdkLoadingPromise = null;

            if (window.postData) {
              console.log('[FTD] postData function available');
              setIsLoading(false);
              setSdkLoaded(true);
              resolve();
            } else {
              console.warn('[FTD] postData function not found');
              setSdkError('Payment form initialization failed');
              reject(new Error('postData not available'));
            }
          }, 500);
        };

        script.onerror = e => {
          console.error('[FTD] Failed to load SDK:', e);
          ftdSdkLoadingPromise = null;
          setIsLoading(false);
          setSdkError('Failed to load payment form. Please try again.');
          reject(e);
        };
      });

      // Append to body
      document.body.appendChild(script);

      // NEVER remove the script - it must persist for postData to work
    }, [ecommToken, elementsReady, getSdkUrl]);

    // Handle form submission and tokenization. Returns the token + card data
    // on success so callers can `await` the result inline. Still invokes the
    // onTokenReceived / onError callbacks for legacy call sites.
    const handleSubmit = useCallback(async () => {
      if (!sdkLoaded || !window.postData) {
        onError('Payment form not ready. Please wait and try again.');

        return null;
      }

      if (!cardholderFirstName || !cardholderLastName) {
        onError('Cardholder name is required');

        return null;
      }

      if (!postalCode) {
        onError('Postal code is required for verification');

        return null;
      }

      // Read the PAN directly from the input (plain DOM — the FTD SDK reads
      // it from there too) so we can derive last-4 + brand before the SDK
      // tokenizes. We never transmit the PAN; only last-4 + brand leave the
      // browser, both of which are PCI-safe to store.
      const panInput = document.getElementById('ccnumber') as HTMLInputElement | null;
      const pan = (panInput?.value || '').replace(/\s/g, '');
      const cardLastFour = pan.slice(-4);
      const cardType = detectCardType(pan);

      try {
        const response = await window.postData();

        if (response.payment_token_id) {
          const cardData: CardData = {
            cardholderFirstName,
            cardholderLastName,
            postalCode,
            cardLastFour,
            cardType,
          };
          onTokenReceived(response.payment_token_id, cardData);

          return {
            paymentTokenId: response.payment_token_id,
            cardData,
          };
        }

        onError(
          response.error ||
            response.message ||
            'Failed to process card. Please check your card details.'
        );

        return null;
      } catch (err: any) {
        console.error('[FTD] Tokenization error:', err);
        onError(err?.message || 'Failed to process card. Please try again.');

        return null;
      }
    }, [sdkLoaded, cardholderFirstName, cardholderLastName, postalCode, onTokenReceived, onError]);

    // Handle save card checkbox
    const handleSaveCardChange = useCallback(
      (value: boolean) => {
        setSaveCard(value);
        onSaveCardChange?.(value);
      },
      [onSaveCardChange]
    );

    // Expose tokenize method to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        tokenize: handleSubmit,
      }),
      [handleSubmit]
    );

    // Format card number with spaces every 4 digits
    const handleCardNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
      value = value.substring(0, 19); // Max 19 digits (some cards have 19)

      // Add spaces every 4 digits
      const parts = [];

      for (let i = 0; i < value.length; i += 4) {
        parts.push(value.substring(i, i + 4));
      }

      e.target.value = parts.join(' ');
    }, []);

    // Format expiry as MMYY - SDK expects no spaces or slashes
    const handleExpiryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
      value = value.substring(0, 4); // Max 4 digits (MMYY)

      if (value.length >= 2) {
        // Validate month (01-12)
        let month = parseInt(value.substring(0, 2), 10);
        if (month > 12) month = 12;
        if (month < 1 && value.length >= 2) month = 1;
        value = month.toString().padStart(2, '0') + value.substring(2);
      }

      e.target.value = value;
    }, []);

    // Limit CVV to 4 digits
    const handleCvvChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
      value = value.substring(0, 4); // Max 4 digits (Amex has 4)
      e.target.value = value;
    }, []);

    if (sdkError) {
      return (
        <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/30">
          <p className="text-sm text-red-700 dark:text-red-300">{sdkError}</p>
        </div>
      );
    }

    // Don't block rendering - the SDK needs to find the input elements when it loads
    // Show a loading indicator alongside the form instead

    const inputClassName =
      'block w-full h-9 rounded-md border border-gray-300 px-3 py-1 text-base md:text-sm shadow-xs placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500 bg-white';

    return (
      <div className="space-y-3">
        {/* Loading indicator */}
        {isLoading && (
          <div className="flex items-center py-1 text-sm text-gray-500">
            <Loading color="text-gray-500" size={4} />
            <span className="ml-2">Initializing secure payment form...</span>
          </div>
        )}

        {/* FTD Card Entry Container - the SDK converts these inputs into secure fields */}
        <div ref={containerRef}>
          {/* Card Number, Expiry, CVV in one row. 12-col grid (6/3/3) — the
              previous `col-span-1.5` was not a valid Tailwind class. */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-6">
              <label
                htmlFor="ccnumber"
                className="block text-sm font-medium leading-5 text-gray-900 dark:text-gray-300 mb-1"
              >
                Card Number *
              </label>
              <input
                id="ccnumber"
                type="text"
                inputMode="numeric"
                autoComplete="cc-number"
                className={inputClassName}
                placeholder="1234 5678 9012 3456"
                disabled={disabled || isSubmitting}
                onChange={handleCardNumberChange}
                maxLength={23}
              />
            </div>
            <div className="col-span-3">
              <label
                htmlFor="ccexpiry"
                className="block text-sm font-medium leading-5 text-gray-900 dark:text-gray-300 mb-1"
              >
                Expiry *
              </label>
              <input
                id="ccexpiry"
                type="text"
                inputMode="numeric"
                autoComplete="cc-exp"
                className={inputClassName}
                placeholder="MMYY"
                disabled={disabled || isSubmitting}
                onChange={handleExpiryChange}
                maxLength={4}
              />
            </div>
            <div className="col-span-3">
              <label
                htmlFor="cccvv"
                className="block text-sm font-medium leading-5 text-gray-900 dark:text-gray-300 mb-1"
              >
                CVV *
              </label>
              <input
                id="cccvv"
                type="text"
                inputMode="numeric"
                autoComplete="cc-csc"
                className={inputClassName}
                placeholder="CVV"
                disabled={disabled || isSubmitting}
                onChange={handleCvvChange}
                maxLength={4}
              />
            </div>
          </div>
        </div>

        {/* Cardholder Name + AVS in two rows */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            name="cardholderFirstName"
            label="First Name *"
            value={cardholderFirstName}
            onChange={setCardholderFirstName}
            disabled={disabled || isSubmitting}
            placeholder="John"
            autoComplete="cc-given-name"
          />
          <Input
            name="cardholderLastName"
            label="Last Name *"
            value={cardholderLastName}
            onChange={setCardholderLastName}
            disabled={disabled || isSubmitting}
            placeholder="Smith"
            autoComplete="cc-family-name"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            name="postalCode"
            label="ZIP Code *"
            value={postalCode}
            onChange={setPostalCode}
            disabled={disabled || isSubmitting}
            placeholder="12345"
            autoComplete="postal-code"
          />
          <div />
        </div>

        {/* Save Card + Security notice inline */}
        <div className="flex items-center justify-between">
          {showSaveCard ? (
            <Checkbox
              name="saveCard"
              label="Save this card for future payments"
              value={saveCard}
              onChange={handleSaveCardChange}
              disabled={disabled || isSubmitting}
            />
          ) : (
            <div />
          )}
          <div className="flex items-center text-xs text-gray-400 dark:text-gray-500">
            <LockClosedIcon className="h-3.5 w-3.5 mr-1" />
            <span>Secured by iPOSpays</span>
          </div>
        </div>

        {/* Hidden submit button for FTD SDK - it looks for id="payButton" */}
        <input type="submit" id="payButton" className="hidden" />
      </div>
    );
  }
);

export default DejavooCardEntry;
